import * as React from "react";
import { Handle, NodeToolbar, Position, type NodeProps } from "@xyflow/react";

import { TESSERA_BRAND_LOGO_WHITE_SRC } from "@/constants/sidebarBrandLogo";
import { cn } from "@/lib/utils";
import {
  WORKSPACE_ACCOUNT_CARD_INNER_SIZE,
  WORKSPACE_ACCOUNT_CARD_SIZE,
  WORKSPACE_NODE_CARD_HEIGHT,
  WORKSPACE_NODE_CARD_WIDTH,
} from "../workspaceGraphConstants";
import {
  workspaceNodeIsPolicyFork,
  workspaceNodeIsSimulationRerun,
  type WorkspaceFlowNodeData,
} from "../workspaceGraphUtils";
import { WorkspaceEntityCardContent, workspaceEntityCardTypeLabel } from "../WorkspaceEntityCardContent";
import { WorkspaceNodeActionsMenu } from "../WorkspaceNodeActions";
import { WorkspacePlaceholderNode } from "./WorkspacePlaceholderNode";
import { WorkspaceInsertNode } from "./WorkspaceInsertNode";
import { WorkspaceExecutingAccountNode } from "./WorkspaceExecutingAccountNode";
import {
  WORKSPACE_INSERT_NODE_TYPE,
  WORKSPACE_PLACEHOLDER_NODE_TYPE,
} from "../workspacePlaceholderUtils";
import { WORKSPACE_EXECUTING_ACCOUNT_NODE_TYPE } from "../workspaceExecutingAccountUtils";
import {
  useWorkspaceNodeToolbarSide,
  WORKSPACE_NODE_TOOLBAR_OFFSET_PX,
} from "../useWorkspaceNodeToolbarSide";
import type { WorkspaceNodeType } from "../../../../types/workspace";
import {
  workspaceNodeTypeStrokeClass,
  workspaceNodeTypeStrokeStyle,
  WORKSPACE_POLICY_COMBINED_SOURCE_HIGHLIGHT_CLASS,
} from "./workspaceNodeIcon";

const EDGE_HANDLE_CLASS =
  "!pointer-events-none !h-px !w-px !min-h-0 !min-w-0 !border-0 !bg-transparent !opacity-0";

function PipelineHandles({
  showTarget,
  showSource,
  showVerticalStack,
}: {
  showTarget: boolean;
  showSource: boolean;
  showVerticalStack?: boolean;
}): React.ReactElement {
  return (
    <>
      {showTarget ? (
        <Handle
          type="target"
          position={Position.Left}
          id="target"
          className={EDGE_HANDLE_CLASS}
          style={{ left: 0, top: "50%", transform: "translate(-50%, -50%)" }}
        />
      ) : null}
      {showSource ? (
        <Handle
          type="source"
          position={Position.Right}
          id="source"
          className={EDGE_HANDLE_CLASS}
          style={{ right: 0, top: "50%", transform: "translate(50%, -50%)" }}
        />
      ) : null}
      {showVerticalStack ? (
        <>
          <Handle
            type="target"
            position={Position.Top}
            id="target-top"
            className={EDGE_HANDLE_CLASS}
            style={{ left: "50%", top: 0, transform: "translate(-50%, -50%)" }}
          />
          <Handle
            type="source"
            position={Position.Bottom}
            id="source-bottom"
            className={EDGE_HANDLE_CLASS}
            style={{ left: "50%", bottom: 0, transform: "translate(-50%, 50%)" }}
          />
        </>
      ) : null}
    </>
  );
}

function cardShellClass(
  type: WorkspaceNodeType,
  isSelected: boolean,
  isDisabled: boolean,
  completionFlash: boolean,
  combinedSourceHighlight = false
): string {
  return cn(
    "h-full overflow-hidden rounded-lg border border-solid bg-card text-card-foreground shadow-sm transition-[box-shadow,border-color] dark:bg-sidebar dark:text-sidebar-foreground",
    workspaceNodeTypeStrokeClass(type),
    isSelected &&
      !completionFlash &&
      !combinedSourceHighlight &&
      "ring-2 ring-primary/45 ring-offset-1 ring-offset-background",
    combinedSourceHighlight &&
      !completionFlash &&
      WORKSPACE_POLICY_COMBINED_SOURCE_HIGHLIGHT_CLASS,
    completionFlash && "data-[workspace-node-flash=aqua]:animate-none",
    isDisabled && "opacity-45 grayscale"
  );
}

function WorkspaceAccountNodeCard({
  isSelected,
  isDisabled,
  focusFilterDimmed = false,
  showTargetHandle = false,
  accountLabel,
}: {
  isSelected: boolean;
  isDisabled: boolean;
  focusFilterDimmed?: boolean;
  /** Federated Strategy view: accounts hang under strategy-root. */
  showTargetHandle?: boolean;
  accountLabel?: string;
}): React.ReactElement {
  const label = accountLabel?.trim() || "";
  return (
    <div
      data-workspace-node=""
      data-workspace-node-type="account"
      data-workspace-node-variant="account-card"
      data-workspace-node-focus-dimmed={focusFilterDimmed ? "true" : undefined}
      className={cn(
        "group relative flex flex-col items-center justify-center gap-1",
        isDisabled && "pointer-events-none",
        focusFilterDimmed && "opacity-40"
      )}
      style={{ width: WORKSPACE_ACCOUNT_CARD_SIZE, height: WORKSPACE_ACCOUNT_CARD_SIZE }}
    >
      <div
        className={cn(
          cardShellClass("account", isSelected, isDisabled, false),
          "flex rotate-45 items-center justify-center !border-white/35 !bg-transparent bg-[image:var(--gradient-primary)] text-white shadow-sm dark:!bg-transparent"
        )}
        data-workspace-node-shell="account"
        style={{
          width: WORKSPACE_ACCOUNT_CARD_INNER_SIZE,
          height: WORKSPACE_ACCOUNT_CARD_INNER_SIZE,
          borderWidth: 1.5,
          borderStyle: "solid",
        }}
      >
        <img
          src={TESSERA_BRAND_LOGO_WHITE_SRC}
          alt={label || "Tessera account"}
          className="size-12 -rotate-45 object-contain"
          draggable={false}
        />
      </div>
      {label ? (
        <span
          data-workspace-account-label=""
          className="pointer-events-none absolute -bottom-6 max-w-[9rem] truncate text-center font-[family-name:var(--font-primary)] text-xs text-muted-foreground"
          title={label}
        >
          {label}
        </span>
      ) : null}
      <PipelineHandles showTarget={showTargetHandle} showSource />
    </div>
  );
}

function WorkspaceEntityNodeCard({
  flowData,
  isSelected,
  isDisabled,
}: {
  flowData: WorkspaceFlowNodeData;
  isSelected: boolean;
  isDisabled: boolean;
}): React.ReactElement {
  const node = flowData.workspaceNode;
  const typeLabel = workspaceEntityCardTypeLabel(node.type);
  const status = node.meta?.status;
  const showTargetHandle = node.type !== "account";
  const isRerun =
    Boolean(flowData.isSimulationRerun) || workspaceNodeIsSimulationRerun(node);
  const isFork =
    Boolean(flowData.isPolicyFork) || workspaceNodeIsPolicyFork(node);
  const showSourceHandle = node.type !== "combined_simulation" || !isRerun;
  const showVerticalStack =
    node.type === "simulation" || node.type === "combined_simulation";
  const isReportGenerating =
    node.type === "report" && status === "processing" && !flowData.dismissing;
  const isSimulationRunning =
    (node.type === "simulation" || node.type === "combined_simulation") &&
    status === "processing" &&
    !flowData.dismissing;
  const completionFlash = Boolean(flowData.completionFlash);
  const dismissing = Boolean(flowData.dismissing);
  const combineSelectionDimmed = Boolean(flowData.combineSelectionDimmed);
  const combineSelectionAnchor = Boolean(flowData.combineSelectionAnchor);
  const combinedSourceHighlight = Boolean(flowData.combinedSourceHighlight);
  const focusFilterDimmed = Boolean(flowData.focusFilterDimmed);

  return (
    <div
      data-workspace-node=""
      data-workspace-node-type={node.type}
      data-workspace-node-variant="entity-card"
      data-workspace-node-status={status ?? undefined}
      data-workspace-node-disabled={isDisabled ? "true" : undefined}
      data-workspace-node-focus-dimmed={focusFilterDimmed ? "true" : undefined}
      data-workspace-node-rerun={isRerun ? "true" : undefined}
      data-workspace-node-fork={isFork ? "true" : undefined}
      data-workspace-node-flash={completionFlash ? "aqua" : undefined}
      data-workspace-node-loading={
        (isReportGenerating || isSimulationRunning) &&
        !isDisabled &&
        !completionFlash &&
        !dismissing
          ? "true"
          : undefined
      }
      data-workspace-node-dismissing={dismissing ? "true" : undefined}
      data-workspace-node-combine-anchor={combineSelectionAnchor ? "true" : undefined}
      data-workspace-node-combine-dimmed={combineSelectionDimmed ? "true" : undefined}
      data-workspace-node-combined-source={combinedSourceHighlight ? "true" : undefined}
      className={cn(
        "group relative",
        isDisabled && "pointer-events-none",
        combineSelectionDimmed && "opacity-40",
        focusFilterDimmed && !combineSelectionDimmed && "opacity-40"
      )}
      style={{ width: WORKSPACE_NODE_CARD_WIDTH, height: WORKSPACE_NODE_CARD_HEIGHT }}
    >
      <div
        data-workspace-node-shell={node.type}
        className={cn(
          cardShellClass(
            node.type,
            isSelected,
            isDisabled,
            completionFlash,
            combinedSourceHighlight
          ),
          "h-full overflow-hidden"
        )}
        style={workspaceNodeTypeStrokeStyle(node.type, { isFork, isRerun })}
      >
        <WorkspaceEntityCardContent
          nodeType={node.type}
          typeLabel={typeLabel}
          title={flowData.label}
          subtitle={flowData.summary}
          detail={flowData.entityCardDetail}
          isRerun={isRerun}
          isFork={isFork}
          isReportGenerating={isReportGenerating}
          isSimulationRunning={isSimulationRunning}
          dismissing={dismissing}
          combineSelectionAnchor={combineSelectionAnchor}
          combinedSourceHighlight={combinedSourceHighlight}
        />
      </div>
      <PipelineHandles
        showTarget={showTargetHandle}
        showSource={showSourceHandle}
        showVerticalStack={showVerticalStack}
      />
    </div>
  );
}

export function WorkspaceFlowNode({ data, dragging }: NodeProps): React.ReactElement {
  const flowData = data as WorkspaceFlowNodeData;
  const node = flowData.workspaceNode;
  const isSelected = Boolean(flowData.selected);
  const showActionsMenu = Boolean(flowData.showActionsMenu && !dragging);
  const isDisabled = Boolean(node.isDisabled);
  const toolbarSide = useWorkspaceNodeToolbarSide(showActionsMenu);
  const focusFilterDimmed = Boolean(flowData.focusFilterDimmed);

  const card =
    node.type === "account" ? (
      <WorkspaceAccountNodeCard
        isSelected={isSelected}
        isDisabled={isDisabled}
        focusFilterDimmed={focusFilterDimmed}
        showTargetHandle={flowData.viewMode === "strategy"}
        accountLabel={
          flowData.viewMode === "strategy"
            ? flowData.label ||
              node.brokerAccountCode ||
              node.meta?.title ||
              node.entityId
            : undefined
        }
      />
    ) : (
      <WorkspaceEntityNodeCard
        flowData={flowData}
        isSelected={isSelected}
        isDisabled={isDisabled}
      />
    );

  return (
    <>
      {card}
      <NodeToolbar
        isVisible={showActionsMenu}
        position={toolbarSide.position}
        align={toolbarSide.align}
        offset={WORKSPACE_NODE_TOOLBAR_OFFSET_PX}
        className="nodrag nopan nowheel"
      >
        <WorkspaceNodeActionsMenu
          node={node}
          nodeTitle={flowData.label}
          accountLabel={flowData.accountLabel}
          mutating={flowData.mutating}
          canDisconnect={flowData.canDisconnect}
          isSimulationRerun={flowData.isSimulationRerun}
          reparentCandidates={flowData.reparentCandidates}
          viewMode={flowData.viewMode}
          onAddChild={flowData.onAddChild}
          onCombinePolicies={flowData.onCombinePolicies}
          onRunCombinedSimulation={flowData.onRunCombinedSimulation}
          onRerunSimulation={flowData.onRerunSimulation}
          onEditSimulationInSimulator={flowData.onEditSimulationInSimulator}
          onAddExecutingAccount={flowData.onAddExecutingAccount}
          onOpenDetail={flowData.onOpenDetail}
          onOpenStrategyWorkspace={flowData.onOpenStrategyWorkspace}
          onOpenOnAccountCanvas={flowData.onOpenOnAccountCanvas}
          onToggleDisabled={flowData.onToggleDisabled}
          onDisconnect={flowData.onDisconnect}
          onReparent={flowData.onReparent}
          onBreakLink={flowData.onBreakLink}
          onClose={flowData.onClearSelection}
        />
      </NodeToolbar>
    </>
  );
}

export const workspaceNodeTypes = {
  workspace: WorkspaceFlowNode,
  [WORKSPACE_PLACEHOLDER_NODE_TYPE]: WorkspacePlaceholderNode,
  [WORKSPACE_INSERT_NODE_TYPE]: WorkspaceInsertNode,
  [WORKSPACE_EXECUTING_ACCOUNT_NODE_TYPE]: WorkspaceExecutingAccountNode,
};
